import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, BookOpen, Trophy, Play, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { topicsApi, Topic } from '../api/topics';

export const TrainingSummary: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await topicsApi.getAll();
        // Take first 3 topics for summary training
        setTopics(Array.isArray(data) ? data.slice(0, 3) : []);
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  const totalFlashcards = Array.isArray(topics) ? topics.reduce((sum, topic) => sum + (topic.flashcards?.length || 0), 0) : 0;
  const totalQuestions = Array.isArray(topics) ? topics.reduce((sum, topic) => sum + (topic.questions?.length || 0), 0) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Verkorte Training
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Een snelle introductie tot AI-geletterdheid met de belangrijkste concepten, 
          flashcards en quizvragen.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="text-center">
          <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 text-brand-primary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Onderwerpen</h3>
          <p className="text-3xl font-bold text-brand-primary">{topics.length}</p>
        </Card>
        <Card className="text-center">
          <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Brain className="w-6 h-6 text-brand-primary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Flashcards</h3>
          <p className="text-3xl font-bold text-brand-primary">{totalFlashcards}</p>
        </Card>
        <Card className="text-center">
          <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-6 h-6 text-brand-primary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Quiz Vragen</h3>
          <p className="text-3xl font-bold text-brand-primary">{totalQuestions}</p>
        </Card>
      </div>

      {/* Topics Overview */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Onderwerpen in deze training</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topics.map((topic, index) => (
            <Card key={topic.id} hover className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold text-brand-primary">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{topic.title}</h3>
                    {topic.summary && (
                      <p className="text-sm text-gray-500 mt-1">{topic.summary}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <Brain className="w-4 h-4" />
                    <span>{topic.flashcards?.length || 0} cards</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Trophy className="w-4 h-4" />
                    <span>{topic.questions?.length || 0} vragen</span>
                  </span>
                </div>

                <Link to={`/topics/${topic.slug}`}>
                  <Button variant="outline" className="w-full flex items-center justify-center space-x-2">
                    <span>Bekijk Onderwerp</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Training Options */}
      <div className="bg-gray-50 rounded-2xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Kies je Training</h2>
          <p className="text-gray-600">
            Start met de verkorte training of ga direct naar een specifiek onderwerp.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="text-center">
            <div className="w-16 h-16 bg-brand-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Training</h3>
            <p className="text-gray-600 mb-6">
              Begin met de eerste onderwerp en werk door alle content, flashcards en quiz.
            </p>
            <Link to={`/topics/${topics[0]?.slug}`}>
              <Button size="lg" className="w-full">
                Start Verkorte Training
              </Button>
            </Link>
          </Card>
          
          <Card className="text-center">
            <div className="w-16 h-16 bg-brand-accent rounded-lg flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Alle Onderwerpen</h3>
            <p className="text-gray-600 mb-6">
              Bekijk alle beschikbare onderwerpen en kies waar je mee wilt beginnen.
            </p>
            <Link to="/topics">
              <Button variant="outline" size="lg" className="w-full">
                Bekijk Alle Onderwerpen
              </Button>
            </Link>
          </Card>
        </div>
      </div>

      {/* Progress Tracking */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Voortgang Tracking
          </h3>
          <p className="text-blue-700 mb-4">
            Je voortgang wordt automatisch opgeslagen. Je kunt altijd stoppen en later verder gaan.
          </p>
          <Link to="/progress">
            <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
              Bekijk Mijn Voortgang
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
};
